import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { api } from "../lib/api";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";

const EASE = [0.16, 1, 0.3, 1];

export default function Insights() {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    api.get("/insights").then((r) => setPosts(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="insights-page" className="pt-32 md:pt-40 pb-24 md:pb-32 text-right">
      <Seo
        title="Insights — Architecture, Climate & Materials Journal"
        description="Long-form writing from Blue Green Studio Bangalore on sustainable architecture, climate-responsive design, alternative construction, courtyard homes and wellness-focused residences."
        path="/insights"
        keywords={[
          "architecture blog Bangalore",
          "sustainable architecture writing",
          "climate responsive design articles",
          "residential architecture journal India",
        ]}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Blog",
          name: "Blue Green Studio Insights",
          description: "Journal of a sustainable architecture practice in Bangalore.",
          blogPost: posts.map((p) => ({
            "@type": "BlogPosting",
            headline: p.title,
            datePublished: p.published_date,
            image: p.cover_image,
            url: `${typeof window !== "undefined" ? window.location.origin : ""}/insights/${p.slug}`,
            author: { "@type": "Organization", name: "Blue Green Studio" },
          })),
        }}
      />

      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE }}
          className="text-xs uppercase tracking-[0.3em] font-bold text-teal mb-6"
        >
          Insights &nbsp;·&nbsp; Journal
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.1 }}
          className="font-serif text-5xl sm:text-6xl lg:text-[6vw] leading-[0.9] tracking-tighter text-ink"
        >
          Notes on <span className="italic text-stone">climate,</span>
          <br />
          <span className="italic text-stone">context</span> and craft.
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.9, delay: 0.3 }}
          className="mt-8 max-w-xl md:ml-auto text-stone text-base leading-relaxed"
        >
          Working notes from our studio in Bangalore — on sustainable architecture, alternative construction, courtyard homes, and the small design decisions that make a residence feel inevitable.
        </motion.p>

        <div className="mt-20 md:mt-28 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14">
          {posts.map((p, i) => {
            const span = i === 0 ? "md:col-span-12" : i % 3 === 0 ? "md:col-span-8" : "md:col-span-6";
            const isFeature = i === 0;
            return (
              <Reveal key={p.slug} delay={(i % 3) * 0.08} className={span}>
                <Link
                  to={`/insights/${p.slug}`}
                  data-testid={`insight-card-${p.slug}`}
                  className="group block"
                >
                  <div className={`clip-frame bg-sand-dark relative ${isFeature ? "aspect-[16/7]" : "aspect-[4/3]"}`}>
                    <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover" />
                    <span className="absolute top-5 left-5 bg-sand/90 backdrop-blur px-4 py-1.5 text-[10px] uppercase tracking-[0.2em] font-bold text-ink">
                      {p.category}
                    </span>
                  </div>
                  <div className="mt-6 flex items-start justify-between gap-6">
                    <div className="text-left flex-1">
                      <h2 className={`font-serif tracking-tight text-ink group-hover:text-teal transition-colors ${isFeature ? "text-4xl md:text-5xl" : "text-2xl md:text-3xl"}`}>
                        {p.title}
                      </h2>
                      <p className="mt-3 text-stone text-sm md:text-base leading-relaxed max-w-2xl">{p.excerpt}</p>
                      <p className="mt-4 text-xs uppercase tracking-[0.2em] text-stone">
                        {new Date(p.published_date).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })} · {p.read_time}
                      </p>
                    </div>
                    <ArrowUpRight
                      className="text-stone group-hover:text-teal group-hover:-translate-y-1 group-hover:translate-x-1 transition-all duration-500 shrink-0"
                      size={30}
                    />
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </div>
    </div>
  );
}
